import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import PaymentForm from './payment_form'
import QuestionnaireReceipt from './receipt'
import RequesterQuestions, { useRequesterQuestions } from './requester_questions'
import PaymentFlowSideBar from './side_bar'
import RequestCanceled from './request_canceled'
import { getQuestionnaireRequestPublicData } from '../api/processor'
import PaymentLoading from './loading'

const STEP = {
  REQUESTER_QUESTIONS: 'requester questions',
  PAYMENT_FORM: 'payment form',
  RECEIPT: 'receipt',
}

function RefundDisclaimer() {
  return (
    <div style={{ paddingTop: '24px' }}>
      <span className="dome-p2 dome-color-dark-grey">
        Prior to disputing any charge with your card issuer, you agree to contact Domecile at{' '}
        <a href="mailto:refunds@domecile.com" className="dome-link dome-link-grey">
          refunds@domecile.com
        </a>{' '}
        with your name, dispute, dispute reason, and any supporting documentation.
      </span>
    </div>
  )
}

const ErrorItem = ({ title, subTitle }) => {
  return (
    <div style={{ padding: 12, borderRadius: 6, background: 'rgba(215, 39, 45, 0.10)' }}>
      <div className="dome-d-flex dome-justify-between dome-align-center">
        <span className="dome-p1 dome-text-w500 dome-color-red-alert">{title}</span>
        <span className="dome-p2 dome-color-darkest-grey">{subTitle}</span>
      </div>
    </div>
  )
}

// TODO: Handle requester cancel and cancelled state

export default function PaymentFlow(props) {
  const {
    hashedId,
    purchaserToken,
    publishableStripeKey,
    userPaymentMethods,
    questionnaireRequest,
    priceTotalInCents,
    formInstanceId,
    workflowType,
    priceInCents,
    processingFeeInCents,
  } = props
  const [step, setStep] = useState(STEP.REQUESTER_QUESTIONS)
  const [priceSummary, setPriceSummary] = useState({ subTotal: 0, fee: 0, total: 0 })
  const [error, setError] = useState(null)
  const [isWaitingVerifyPayment, setIsWaitingVerifyPayment] = useState(true)

  const {
    questions: requesterQuestions,
    values,
    setValues,
  } = useRequesterQuestions({ formId: formInstanceId, workflowType })

  // Synchronizes the current workflow step
  useEffect(() => {
    if (questionnaireRequest.paid) {
      setStep(STEP.RECEIPT)
    } else if (workflowType !== 'automated' || (requesterQuestions && requesterQuestions?.length === 0)) {
      setStep(STEP.PAYMENT_FORM)
      setIsWaitingVerifyPayment(false)
    } else if (requesterQuestions && requesterQuestions?.length > 0) {
      setStep(STEP.REQUESTER_QUESTIONS)
      setIsWaitingVerifyPayment(false)
    }
  }, [questionnaireRequest.paid, requesterQuestions, workflowType])

  useEffect(() => {
    setPriceSummary({
      subTotal: priceInCents,
      fee: processingFeeInCents,
      total: priceTotalInCents,
    })
  }, [priceInCents, processingFeeInCents, priceTotalInCents])

  const handlePaymentFailed = useCallback((message) => setError({ title: 'payment failed', subTitle: message }), [])
  const handlePurchaseSuccess = useCallback(async () => {
    setStep(STEP.RECEIPT)
    setIsWaitingVerifyPayment(true)
  }, [])

  const handleUnpaidReceipt = useCallback(() => {
    setStep(STEP.PAYMENT_FORM)
    handlePaymentFailed('Your card has not been charged. Please try again.')
  }, [handlePaymentFailed])

  useEffect(() => {
    // Verify payment when navigating to receipt
    if (step !== STEP.RECEIPT) return

    const verifyPayment = async () => {
      try {
        const res = await getQuestionnaireRequestPublicData(hashedId)
        if (res.data?.paid && res.data?.paid_at) {
          const priceState = {
            subTotal: res.data?.price_in_cents,
            fee: res.data?.fee_in_cents,
            total: res.data?.price_total_in_cents,
          }
          setPriceSummary(priceState)
        } else {
          handleUnpaidReceipt()
        }
      } catch (error) {
        handleUnpaidReceipt()
      } finally {
        setIsWaitingVerifyPayment(false)
      }
    }

    verifyPayment()
  }, [step, hashedId, handleUnpaidReceipt])

  const status = useMemo(() => {
    switch (step) {
      case STEP.REQUESTER_QUESTIONS:
        return 'info needed'
      case STEP.PAYMENT_FORM:
        return 'needs payment'
      case STEP.RECEIPT:
        return 'paid'
      default:
        return null
    }
  }, [step])

  if (questionnaireRequest.status === 'canceled')
    return (
      <RequestCanceled
        unitId={props.unitId}
        propertyAddress={props.propertyAddress}
        building_address={props.building_address}
        questionnaireRequest={questionnaireRequest}
      />
    )

  return (
    <div className="auto-questionnaire-processing-page-wrapper">
      <div className="auto-questionnaire-processing-page-container">
        <div className="dome-d-flex dome-align-start dome-flex-column dome-gap6">
          <h1 style={{ margin: 0 }} className="dome-h2 dome-color-dark-grey">
            questionnaire request
          </h1>
        </div>
        {isWaitingVerifyPayment ? (
          <PaymentLoading />
        ) : (
          <div className="dome-full-width">
            <div className="payment-flow-container dome-d-flex dome-gap24">
              <div className="dome-d-flex dome-flex-column dome-gap6">
                {error && <ErrorItem title={error.title} subTitle={error.subTitle} />}
                <div className="payment-flow-left-container">
                  <div className="payment-flow-left-container__main">
                    {step === STEP.REQUESTER_QUESTIONS && (
                      <RequesterQuestions
                        questions={requesterQuestions}
                        onSubmit={() => setStep(STEP.PAYMENT_FORM)}
                        formId={questionnaireRequest.form_instance_id}
                        values={values}
                        setValues={setValues}
                      />
                    )}
                    {step === STEP.PAYMENT_FORM && (
                      <PaymentForm
                        hashedId={hashedId}
                        purchaserToken={purchaserToken}
                        userPaymentMethods={userPaymentMethods}
                        publishableStripeKey={publishableStripeKey}
                        priceTotalInCents={priceTotalInCents}
                        hasOnBack={workflowType === 'automated' || requesterQuestions?.length > 0}
                        onBack={() => setStep(STEP.REQUESTER_QUESTIONS)}
                        onSubmit={() => setError(null)}
                        onSuccess={handlePurchaseSuccess}
                        onError={handlePaymentFailed}
                      />
                    )}
                    {step === STEP.RECEIPT && <QuestionnaireReceipt {...props} {...priceSummary} />}
                  </div>
                  {step === STEP.RECEIPT && <RefundDisclaimer />}
                </div>
              </div>
              <div className="payment-flow-right-container" style={{ flexShrink: 0 }}>
                <PaymentFlowSideBar status={status} {...props} {...priceSummary} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

PaymentFlow.propTypes = {
  workflowType: PropTypes.oneOf(['automated', 'manual']),
}
